import type { ColumnCatalogDef } from '@/types/column-catalog'
import type { DataType, FilterEntityFields } from '../schemas/filter-entity'
import {
  isRangeValue,
  isTimeSliceValue,
  OPERATOR_LABELS,
  operatorsForColumn,
  typeClassOf,
} from '../schemas/filter-entity'
import { areColumnsCompatible } from '../schemas/column-compatibility'

/**
 * Validates that a filter entity's right-hand side is compatible with its LHS column.
 *
 * Returns `null` if valid (or validation can't run — e.g. missing catalog entry).
 * Returns an error message string if the entity has a `rightType: 'column'` whose
 * referenced column belongs to a different TypeClass than the LHS.
 *
 * Only `rightType: 'column'` is cross-type checked — value/expression/time_slices
 * right-hand sides are out of scope for this validator.
 */
export function validateCrossType(
  entity: FilterEntityFields,
  columnMap: Record<string, ColumnCatalogDef>,
): string | null {
  if (entity.rightType !== 'column') return null
  if (typeof entity.right !== 'string' || entity.right.length === 0) return null

  const lhs = columnMap[entity.left.column]
  const rhs = columnMap[entity.right]
  if (!lhs || !rhs) return null // can't validate without both sides in the catalog

  if (!areColumnsCompatible(lhs, rhs)) {
    return `Cannot compare "${lhs.label}" with "${rhs.label}"`
  }
  return null
}

/**
 * Validates that a filter entity's operator is valid for its LHS column's dataType.
 *
 * Returns `null` if the pair is valid, or if validation can't run (LHS column
 * not in the catalog). Returns a user-facing error message when the operator
 * is not in `operatorsForColumn(lhs)`.
 *
 * Independent of `rightType` — purely a (column.dataType × operator) check.
 * Runs at two boundaries:
 *   - `profileToQuery`: drops invalid leaves from the outgoing query
 *   - preset-load: disables the row and surfaces the message via toast
 */
export function validateOperatorForColumn(
  entity: FilterEntityFields,
  columnMap: Record<string, ColumnCatalogDef>,
): string | null {
  const lhs = columnMap[entity.left.column]
  if (!lhs) return null

  const validOps = operatorsForColumn(lhs)
  if (validOps.includes(entity.operator)) return null

  const opLabel = OPERATOR_LABELS[entity.operator] ?? entity.operator
  return `Operator "${opLabel}" is not valid for ${lhs.dataType} column "${lhs.label}"`
}

/**
 * Validates that a filter entity's LHS column still exists in the catalog.
 *
 * Returns `null` if the column is present, or if the entity hasn't been
 * fully picked yet (empty column string), or if the catalog is empty
 * (legacy/test path — validation opts out to preserve backward compat).
 *
 * Returns an error message when the column key references a column that
 * has since been removed/renamed in the catalog. Primary use: preset-load
 * schema-drift detection and serialization-layer belt-and-suspenders.
 *
 * Intentionally LHS-only. RHS column-reference validation for column-to-column
 * comparisons is handled separately by `validateCrossType`.
 */
export function validateColumnExists(
  entity: FilterEntityFields,
  columnMap: Record<string, ColumnCatalogDef>,
): string | null {
  // Empty catalog → can't validate (legacy call paths that never wire it).
  if (Object.keys(columnMap).length === 0) return null
  // Empty column key → the row is not yet configured; don't flag as "missing".
  if (!entity.left.column) return null
  if (columnMap[entity.left.column]) return null
  return `Column "${entity.left.column}" no longer exists in the catalog`
}

function expectedValueLabel(dataType: DataType): string {
  switch (typeClassOf(dataType)) {
    case 'numeric':
      return 'number'
    case 'categorical':
      return 'string'
    case 'temporal':
      return 'string (date)'
    case 'boolean':
      return 'boolean'
  }
}

function matchesNumericShape(right: unknown): boolean {
  if (typeof right === 'number') return true
  if (isRangeValue(right)) {
    return typeof right.low === 'number' && typeof right.high === 'number'
  }
  if (Array.isArray(right)) {
    return right.every((v) => typeof v === 'number')
  }
  return false
}

function matchesCategoricalShape(right: unknown): boolean {
  if (typeof right === 'string') return true
  if (Array.isArray(right)) {
    return right.every((v) => typeof v === 'string')
  }
  return false
}

function matchesTemporalShape(right: unknown): boolean {
  if (typeof right === 'string') return true
  if (isRangeValue(right)) {
    return typeof right.low === 'string' && typeof right.high === 'string'
  }
  return false
}

/**
 * Validates that a filter entity's RHS value shape matches the LHS column's
 * dataType class. Only fires for `rightType: 'value'` — other right-types are
 * shaped by their own contracts (column: string key, expression: string, etc.).
 *
 * Returns `null` when validation can't run (missing column, empty catalog,
 * synthetic boolean ops, time-slice values) or when the shape matches.
 * Returns an error message on mismatch — same silent-drop policy at
 * serialization as the sibling validators.
 */
export function validateValueType(
  entity: FilterEntityFields,
  columnMap: Record<string, ColumnCatalogDef>,
): string | null {
  if (entity.rightType !== 'value') return null
  if (entity.right === null) return null
  if (entity.operator === 'is_true' || entity.operator === 'is_false') return null
  if (isTimeSliceValue(entity.right)) return null

  if (Object.keys(columnMap).length === 0) return null
  if (!entity.left.column) return null

  const lhs = columnMap[entity.left.column]
  if (!lhs) return null

  if (Array.isArray(entity.right) && entity.operator !== 'in' && entity.operator !== 'not_in') {
    return `"${OPERATOR_LABELS[entity.operator] ?? entity.operator}" takes a single value, not a list`
  }

  // Wall-clock columns (`string_time`) take a clock string or a low/high pair
  // of them for `between`; the plain string shape has no range form.
  if (lhs.columnType === 'string_time') {
    return matchesTemporalShape(entity.right)
      ? null
      : `Value type doesn't match "${lhs.label}" (expected time)`
  }

  const cls = typeClassOf(lhs.dataType)
  let ok = false
  if (cls === 'numeric') ok = matchesNumericShape(entity.right)
  else if (cls === 'categorical') ok = matchesCategoricalShape(entity.right)
  else if (cls === 'temporal') ok = matchesTemporalShape(entity.right)
  else if (cls === 'boolean') ok = true // boolean ops short-circuit above

  if (ok) return null
  return `Value type doesn't match "${lhs.label}" (expected ${expectedValueLabel(lhs.dataType)})`
}

/**
 * Runs every row-level validator in priority order and returns the first
 * non-null error, or null when the row is clean. Priority:
 *   1. `validateColumnExists` — hardest failure, short-circuits the rest
 *   2. `validateCrossType`    — RHS column of incompatible class
 *   3. `validateOperatorForColumn` — op not legal for LHS dataType
 *   4. `validateValueType`    — RHS value shape doesn't match LHS class
 *
 * Used by `FilterRow` to render a single inline error badge that reflects
 * whichever issue is most blocking.
 */
export function firstRowValidationError(
  entity: FilterEntityFields,
  columnMap: Record<string, ColumnCatalogDef>,
): string | null {
  return (
    validateColumnExists(entity, columnMap) ??
    validateCrossType(entity, columnMap) ??
    validateOperatorForColumn(entity, columnMap) ??
    validateValueType(entity, columnMap)
  )
}
